import { z } from 'zod';

import type { Role } from './enums.js';

export const COMMUNICATION_COACH_ROLES: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
  'assistant_coach',
  'team_manager',
]);

export const COMMUNICATION_MEMBER_ROLES: ReadonlySet<Role> = new Set<Role>([
  ...COMMUNICATION_COACH_ROLES,
  'guardian',
]);

export const CALENDAR_SUBSCRIBER_ROLES: ReadonlySet<Role> = new Set<Role>([
  ...COMMUNICATION_COACH_ROLES,
  'guardian',
]);

export function canSendAnnouncement(roles: readonly Role[]): boolean {
  return roles.some((role) => COMMUNICATION_COACH_ROLES.has(role));
}

export const canViewAnnouncementRollup = canSendAnnouncement;
export const canNudgeAnnouncement = canSendAnnouncement;
export const canModerateThread = canSendAnnouncement;

export function canAcknowledgeAnnouncement(roles: readonly Role[]): boolean {
  return roles.some((role) => COMMUNICATION_MEMBER_ROLES.has(role));
}

export function canWriteThread(roles: readonly Role[]): boolean {
  return roles.some((role) => COMMUNICATION_MEMBER_ROLES.has(role));
}

export function canSubscribeCalendar(roles: readonly Role[]): boolean {
  return roles.some((role) => CALENDAR_SUBSCRIBER_ROLES.has(role));
}

/**
 * Who may change a message after it is sent (ADR-0122).
 *
 * An announcement: its author, or an Owner or Head Coach of the team. The two
 * roles that answer for what the team says, not every role that may send.
 * A question or a reply is edited by its author alone, and removed by its
 * author or by a moderator, which is the authority the Remove control held.
 */
export const ANNOUNCEMENT_OVERSEER_ROLES: ReadonlySet<Role> = new Set<Role>([
  'owner',
  'head_coach',
]);

export function canChangeAnnouncement(roles: readonly Role[], isAuthor: boolean): boolean {
  return isAuthor || roles.some((role) => ANNOUNCEMENT_OVERSEER_ROLES.has(role));
}

export function canRemoveThreadContent(roles: readonly Role[], isAuthor: boolean): boolean {
  return isAuthor || canModerateThread(roles);
}

export const AnnouncementKindSchema = z.enum(['broadcast', 'schedule_change']);
export type AnnouncementKind = z.infer<typeof AnnouncementKindSchema>;

export const AnnouncementCreateSchema = z.object({
  body: z.string().trim().min(1).max(4_000),
});
export type AnnouncementCreate = z.infer<typeof AnnouncementCreateSchema>;

export const ThreadCreateSchema = z.object({
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(4_000),
});
export type ThreadCreate = z.infer<typeof ThreadCreateSchema>;

export const ThreadPostCreateSchema = z.object({
  body: z.string().trim().min(1).max(4_000),
});
export type ThreadPostCreate = z.infer<typeof ThreadPostCreateSchema>;

/** An edit carries the whole new text; the same limits as a new message. */
export const MessageEditSchema = ThreadPostCreateSchema;
export type MessageEdit = z.infer<typeof MessageEditSchema>;

export const ThreadEditSchema = z.object({
  title: z.string().trim().min(1).max(120),
});
export type ThreadEdit = z.infer<typeof ThreadEditSchema>;

const PUSH_HOSTS = [
  'fcm.googleapis.com',
  'updates.push.services.mozilla.com',
  'web.push.apple.com',
  'push.apple.com',
  'notify.windows.com',
  'android.googleapis.com',
];

/** An explicit default port is the one spelling tolerated beyond the parser's own. */
const EXPLICIT_DEFAULT_PORT = /^(https:\/\/[^/?#]+):443(?=[/?#]|$)/;

/**
 * The endpoint as the WHATWG parser would send it, or null.
 *
 * Only the literal `https://` prefix and the parser's own href are accepted, so
 * no second parser (web-push reads endpoints with legacy `url.parse`) can read a
 * different host out of the same string.
 */
export function canonicalPushEndpoint(value: string): string | null {
  if (!value.startsWith('https://') || !URL.canParse(value)) return null;
  const url = new URL(value);
  const allowed =
    url.username === '' &&
    url.password === '' &&
    url.port === '' &&
    PUSH_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`));
  return allowed && url.href === value.replace(EXPLICIT_DEFAULT_PORT, '$1') ? url.href : null;
}

export const PushSubscriptionSchema = z.object({
  endpoint: z
    .url()
    .max(2_048)
    .transform((value, ctx) => {
      const href = canonicalPushEndpoint(value);
      if (href === null) {
        ctx.addIssue({ code: 'custom', message: 'Not a push service endpoint.' });
        return z.NEVER;
      }
      return href;
    }),
  keys: z.object({
    p256dh: z.string().min(1).max(512),
    auth: z.string().min(1).max(512),
  }),
});
export type PushSubscriptionInput = z.infer<typeof PushSubscriptionSchema>;

export const NotificationPayloadSchema = z.object({
  category: z.enum(['announcement', 'schedule_change', 'thread_reply']),
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(4_000),
  deepLink: z.string().regex(/^\/(?![/\\])[^\\\s]*$/),
});
export type NotificationPayload = z.infer<typeof NotificationPayloadSchema>;
